// One place where the task page sends writes (T21): one write at a time, the version the person
// saw as If-Match, and a 409 version conflict kept so the "changed by someone else" dialog can
// show what changed and reapply the same change on the new version (constitution §4.7, §8.4).
import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isApiError, type TaskDetail } from '@cis/api-client';
import {
  taskDetailKey,
  useTaskWrite,
  useTaskWritePending,
  type TaskWrite,
} from '../../api/tasks.ts';
import { userMessage } from '../../ui/error-message.ts';
import { useToast } from '../../ui/toast.tsx';
import { writeDoneText } from './action-labels.ts';

export type WriteOutcome = 'ok' | 'conflict' | 'failed' | 'busy';

export interface Conflict {
  readonly write: TaskWrite;
  /** The page as the person saw it when they acted. */
  readonly seen: TaskDetail;
}

export interface TaskWriter {
  readonly pending: boolean;
  readonly conflict: Conflict | null;
  /**
   * Sends the write with the version of `seen` (default: the page now). A form that edits text
   * passes the page it opened on when those fields changed since, so the 409 dialog shows it
   * instead of the save silently replacing someone else's text (constitution §4.7).
   */
  send(write: TaskWrite, seen?: TaskDetail): Promise<WriteOutcome>;
  /** Sends the conflicting write again on the version now on screen. */
  reapply(current: TaskDetail): Promise<WriteOutcome>;
  dismiss(): void;
  /** The message of the last write that failed (not a 409), so a form can show it in place. */
  lastError(): string | null;
}

/**
 * The page a dialog sends against: the page now when the fields it edits are as the person
 * opened them (a tick elsewhere does not matter), else the opened page, so the API answers 409
 * and the dialog shows what changed instead of replacing someone else's value.
 */
export function baseFor(
  opened: TaskDetail,
  now: TaskDetail,
  edited: (d: TaskDetail) => unknown,
): TaskDetail {
  return JSON.stringify(edited(opened)) === JSON.stringify(edited(now)) ? now : opened;
}

export function useTaskWriter(detail: TaskDetail): TaskWriter {
  const taskId = detail.taskId;
  const queryClient = useQueryClient();
  const toast = useToast();
  const mutation = useTaskWrite(taskId);
  const pending = useTaskWritePending(taskId);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  // Set before the request starts, so a second click in the same frame cannot send again (§5.5).
  const inFlight = useRef(false);
  const failure = useRef<string | null>(null);

  const run = useCallback(
    async (write: TaskWrite, seen: TaskDetail): Promise<WriteOutcome> => {
      if (inFlight.current) return 'busy';
      inFlight.current = true;
      failure.current = null;
      try {
        await mutation.mutateAsync({ write, version: seen.state.version });
        toast.show('success', writeDoneText(write));
        return 'ok';
      } catch (err) {
        if (isApiError(err, 'version_conflict')) {
          // Read the newest version first, so the dialog compares with what is saved now.
          await queryClient.invalidateQueries({ queryKey: taskDetailKey(taskId, undefined) });
          setConflict({ write, seen });
          return 'conflict';
        }
        failure.current = userMessage(err);
        toast.show('error', failure.current);
        return 'failed';
      } finally {
        inFlight.current = false;
      }
    },
    [mutation, queryClient, taskId, toast],
  );

  return {
    pending: pending || mutation.isPending,
    conflict,
    send: (write, seen) => run(write, seen ?? detail),
    reapply: async (current) => {
      if (conflict === null) return 'failed';
      const write = conflict.write;
      setConflict(null);
      return run(write, current);
    },
    dismiss: () => setConflict(null),
    lastError: () => failure.current,
  };
}
